import api from '../utils/api';

/**
 * Get all firmware versions with pagination and filters
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number
 * @param {number} params.pageSize - Items per page
 * @param {string} params.searchTerm - Search term
 * @param {string} params.sortColumn - Column to sort by
 * @param {string} params.sortDirection - Sort direction (asc/desc)
 * @returns {Promise<Object>} Response with data and meta
 */
export const getAllFirmwareVersions = async (params) => {
  const response = await api.get('/firmwareversions', params);
  return response;
};

/**
 * Get firmware version by ID
 * @param {number} id - Firmware version ID
 * @returns {Promise<Object>} Firmware version data
 */
export const getFirmwareVersionById = async (id) => {
  const response = await api.get(`/firmwareversions/${id}`);
  return response;
};

/**
 * Get initialization data for firmware version form
 * @returns {Promise<Object>} Init data
 */
export const getFirmwareVersionInit = async () => {
  const response = await api.get('/firmwareversions/init');
  return response;
};

/**
 * Create a new firmware version with file upload
 * @param {Object} data - Firmware version data
 * @param {string} data.version - Version number
 * @param {File} data.file - Firmware file
 * @param {number} data.deviceType - Device type
 * @param {string} data.note - Optional note
 * @returns {Promise<Object>} Created firmware version
 */
export const createFirmwareVersion = async (data) => {
  const formData = new FormData();
  formData.append('version', data.version);
  formData.append('file', data.file);
  formData.append('deviceType', data.deviceType);
  if (data.note) {
    formData.append('note', data.note);
  }

  const response = await api.post('/firmwareversions', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response;
};

/**
 * Update an existing firmware version
 * @param {number} id - Firmware version ID
 * @param {Object} data - Firmware version data
 * @param {string} data.version - Version number
 * @param {File} data.file - Optional firmware file (if updating file)
 * @param {number} data.deviceType - Device type
 * @param {string} data.note - Optional note
 * @returns {Promise<Object>} Updated firmware version
 */
export const updateFirmwareVersion = async (id, data) => {
  const formData = new FormData();
  formData.append('id', id);
  formData.append('version', data.version);
  if (data.file) {
    formData.append('file', data.file);
  }
  formData.append('deviceType', data.deviceType);
  if (data.note) {
    formData.append('note', data.note);
  }

  const response = await api.put(`/firmwareversions/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response;
};

/**
 * Delete a firmware version
 * @param {number} id - Firmware version ID
 * @returns {Promise<Object>} Deletion response
 */
export const deleteFirmwareVersion = async (id) => {
  const response = await api.delete(`/firmwareversions/${id}`);
  return response;
};

/**
 * Bulk delete firmware versions
 * @param {Array<number>} ids - Array of firmware version IDs
 * @returns {Promise<Object>} Deletion response
 */
export const bulkDeleteFirmwareVersions = async (ids) => {
  // Send as multiple query params: ?ids=4&ids=2 instead of ?ids=4,2
  const queryString = ids.map(id => `ids=${id}`).join('&');
  const response = await api.delete(`/firmwareversions?${queryString}`);
  return response;
};
